import { useState, useEffect, useCallback, useMemo } from 'react';
import { Check, Star, ArrowRight, Loader2, Zap, Shield, RefreshCw } from 'lucide-react';
import { getAnalyzerUrl, getBackendUrl } from '../../config/api';
import ScrollReveal from './ScrollReveal';

const FREE_FEATURES_BASE = [
  null, // first line from config: "X analysis credits/day (auto-refill)"
  'Standard crawl analysis',
  'Crawler Vision Score',
  'Technical SEO audit',
  'GEO & AIO readiness scoring',
  'Meta relevancy check',
  'AI files evaluation',
  'Shareable HTML reports',
];

const PREMIUM_FEATURES = [
  'Everything in Free, plus:',
  'Configurable credit multiplier',
  'RENDERED JS rendering engine',
  'LLM visibility check (5 AI models)',
  'AI-powered suggestions (Claude)',
  'PageSpeed Insights integration',
  'URL monitoring & scheduling',
  'Premium Dashboard',
  'PDF report downloads',
  'Reserve credit pool (purchasable)',
  'Priority support',
];

const PricingSection = () => {
  const [config, setConfig] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [multiplier, setMultiplier] = useState(1);
  const analyzerUrl = getAnalyzerUrl();

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${getBackendUrl()}/api/premium/pricing-config`);
        if (res.ok) {
          const data = await res.json();
          if (data.success) setConfig(data.config);
        }
      } catch { /* fallback to defaults */ }
    };
    fetchConfig();
  }, []);

  const fetchPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${getBackendUrl()}/api/premium/calculate-price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ multiplier, billing_cycle: billingCycle, reserve_credits: 0, country: 'US' }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setPricing(data.pricing);
      }
    } catch { /* fallback */ }
    finally { setLoading(false); }
  }, [multiplier, billingCycle]);

  useEffect(() => { fetchPricing(); }, [fetchPricing]);

  const multipliers = useMemo(() => {
    const source = pricing?.multipliers ?? config?.multipliers ?? [];
    const values = source.map((m) => Number(m?.value)).filter((v) => Number.isFinite(v) && v > 0);
    return values.length > 0 ? values : [1, 2, 3, 5, 10];
  }, [config?.multipliers, pricing?.multipliers]);

  useEffect(() => {
    if (multipliers.length > 0 && !multipliers.includes(multiplier)) {
      setMultiplier(multipliers[0]);
    }
  }, [multipliers, multiplier]);

  // SSOT: pull the 1x base price from the backend's calculate-price
  // response when present, then from the pricing-config fallback. We
  // intentionally do NOT hardcode 9.99 anymore — if both upstreams
  // fail we render "—" rather than fabricate a price.
  const oneX = (pricing?.multipliers ?? config?.multipliers ?? []).find((m) => Number(m?.value) === 1);
  const baseMonthly = oneX != null && Number.isFinite(Number(oneX?.price_usd)) ? Number(oneX.price_usd) : null;
  // Backend may expose fraction (0.2) or admin/KV may use 0-100 (20). calculate-price bug used to emit 2000% when KV had 20.
  const yearlyDiscountPercent = (() => {
    if (pricing?.yearly_discount_percent != null) {
      let p = Number(pricing.yearly_discount_percent);
      if (Number.isFinite(p) && p > 100) p = p / 100; // e.g. 2000 from 20 * 100
      return p;
    }
    if (config?.yearly_discount == null) return null;
    const yd = Number(config.yearly_discount);
    if (!Number.isFinite(yd) || yd < 0) return null;
    return yd >= 1 ? yd : yd * 100;
  })();
  const symbol = pricing?.symbol || '$';
  // For yearly, the backend's `subscription_price` is the PRE-discount
  // 12-month subtotal; the yearly saving lives in `discount_total`.
  const subPrice = pricing?.subscription_price;
  const discountTotal = Number(pricing?.discount_total ?? 0);
  const approxPerDay = pricing?.approx_per_day;
  const credits = approxPerDay ?? pricing?.base_credits ?? (20 * multiplier);
  const llmDaily = pricing?.llm_daily_limit ?? (multiplier >= 5 ? 20 : 10);
  const isYearly = billingCycle === 'yearly';

  // SSOT: derive the headline monthly price from the backend quote.
  // Yearly billing must surface the discount as a lower effective
  // monthly cost (with the full monthly rate struck through) so the
  // toggle actually reads as an advantage / CTA. We use
  // `subscription_price - discount_total` (both subscription-level on
  // this card — reserve=0, no coupon) so the math is tax-agnostic and
  // never recomputes the discount client-side. If the backend can't be
  // reached we fall back transparently to pricing-config; otherwise we
  // render the "—" / loading state.
  let perMonth = null; // headline number the customer effectively pays per month
  let originalPerMonth = null; // struck-through full monthly rate (yearly only)
  let billedAnnual = null; // "billed $X/yr" pill (yearly only)
  let annualSaving = null; // "you save $Y/yr" callout (yearly only)

  if (subPrice != null) {
    if (isYearly) {
      const annualSubtotal = subPrice; // pre-discount 12-month subtotal
      const discountedAnnual = Math.max(0, annualSubtotal - discountTotal);
      perMonth = discountedAnnual / 12;
      originalPerMonth = annualSubtotal / 12; // == full monthly rate
      billedAnnual = discountedAnnual;
      annualSaving = annualSubtotal - discountedAnnual;
    } else {
      perMonth = subPrice;
    }
  } else if (baseMonthly != null && (!isYearly || yearlyDiscountPercent != null)) {
    const monthlyRate = baseMonthly * multiplier;
    if (isYearly) {
      const pct = yearlyDiscountPercent || 0;
      perMonth = monthlyRate * (1 - pct / 100);
      originalPerMonth = monthlyRate;
      billedAnnual = monthlyRate * 12 * (1 - pct / 100);
      annualSaving = monthlyRate * 12 * (pct / 100);
    } else {
      perMonth = monthlyRate;
    }
  }

  return (
    <section id="pricing" className="py-20 md:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <ScrollReveal>
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 bg-cv-sky/10 text-cv-navy text-xs font-bold uppercase tracking-wider rounded-full border border-cv-sky/30 mb-4">
              Pricing
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-cv-navy mb-4">
              Simple, Transparent{' '}
              <span className="text-cv-navy">Pricing</span>
            </h2>
            <p className="text-cv-navy/70 max-w-xl mx-auto text-lg">
              Start for free — upgrade when you need advanced features. No hidden fees, cancel anytime.
            </p>
          </div>
        </ScrollReveal>

        {/* Billing toggle */}
        <ScrollReveal>
          <div className="flex items-center justify-center gap-3 mb-10">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                !isYearly ? 'bg-cv-navy text-white shadow-md' : 'bg-cv-navy/10 text-cv-navy/70 hover:bg-cv-navy/15'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                isYearly ? 'bg-cv-navy text-white shadow-md' : 'bg-cv-navy/10 text-cv-navy/70 hover:bg-cv-navy/15'
              }`}
            >
              Yearly
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                isYearly ? 'bg-white/20 text-white' : 'bg-cv-gold/30 text-cv-navy'
              }`}>
                Save {yearlyDiscountPercent != null ? yearlyDiscountPercent : '—'}%
              </span>
            </button>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          {/* Free tier */}
          <ScrollReveal delay={0}>
            <div data-cv-tab="pricing-free" className="bg-white rounded-2xl shadow-lg border border-cv-navy/10 p-8 h-full flex flex-col">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-cv-navy mb-1">Free</h3>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-4xl font-extrabold text-cv-navy">$0</span>
                  <span className="text-sm text-cv-navy/60">forever</span>
                </div>
                <p className="text-sm text-cv-navy/70 leading-relaxed">
                  Get started with no signup required — analyze any website instantly.
                </p>
              </div>
              <ul className="space-y-3 mb-8 flex-1">
                {FREE_FEATURES_BASE.map((f, i) => {
                  const text = i === 0
                    ? (config?.free_tier?.approx_per_day != null
                      ? `Up to ${Math.round(config.free_tier.approx_per_day)} analysis credits/day (auto-refill)`
                      : config?.free_tier?.base_credits != null
                        ? `${config.free_tier.base_credits} analysis credits/day (auto-refill)`
                        : '20 analysis credits/day (auto-refill)')
                    : f;
                  return (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-cv-navy" />
                      <span className="text-cv-navy/80 leading-snug">{text}</span>
                    </li>
                  );
                })}
              </ul>
              <a
                href={analyzerUrl}
                className="w-full py-3.5 px-6 rounded-xl font-bold text-center text-sm transition-all flex items-center justify-center gap-2 bg-cv-navy text-white hover:shadow-lg shadow-sm"
              >
                Start Free Analysis <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </ScrollReveal>

          {/* Premium tier */}
          <ScrollReveal delay={0.1}>
            <div data-cv-tab="pricing-premium" className="relative bg-cv-navy text-white rounded-2xl shadow-2xl shadow-cv-navy/20 ring-2 ring-cv-navy p-8 h-full flex flex-col">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-cv-gold/70 text-xs font-bold uppercase tracking-wider text-cv-navy rounded-full shadow-md flex items-center gap-1">
                <Star className="w-3 h-3" /> Most Popular
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-1">Premium</h3>
                <div className="flex items-baseline flex-wrap gap-x-1.5 gap-y-1 mb-2">
                  {loading || perMonth == null ? (
                    <Loader2 className="w-6 h-6 animate-spin text-white/85" />
                  ) : (
                    <>
                      {isYearly && originalPerMonth != null && originalPerMonth > perMonth && (
                        <span className="text-xl font-semibold text-white/40 line-through mr-0.5">
                          {symbol}{originalPerMonth.toFixed(2)}
                        </span>
                      )}
                      <span className="text-4xl font-extrabold">
                        {symbol}{perMonth.toFixed(2)}
                      </span>
                      <span className="text-sm text-white/85">/month</span>
                      {isYearly && billedAnnual != null && (
                        <span className="ml-2 text-xs bg-white/15 px-2 py-0.5 rounded-full text-white/85">
                          billed {symbol}{billedAnnual.toFixed(2)}/yr
                        </span>
                      )}
                    </>
                  )}
                </div>
                {!loading && isYearly && annualSaving != null && annualSaving > 0 && (
                  <p className="text-xs font-bold text-cv-gold mb-3 flex items-center gap-1">
                    <Zap className="w-3 h-3" /> You save {symbol}{annualSaving.toFixed(2)}/yr with annual billing
                  </p>
                )}
                <p className="text-sm text-white/85 leading-relaxed">
                  Unlock full power — RENDERED JavaScript rendering, LLM visibility, AI suggestions, and more.
                </p>
              </div>

              {/* Multiplier selector */}
              <div className="mb-5 p-3 bg-white/10 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-white/85 uppercase tracking-wider flex items-center gap-1">
                    <Zap className="w-3 h-3" /> Credit Multiplier
                  </span>
                  <span className="text-xs text-white/85">
                    {typeof credits === 'number' && credits > 0 ? `Up to ${Math.round(credits)} credits/day` : credits} &middot; {llmDaily} LLM/day
                  </span>
                </div>
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-1.5">
                  {multipliers.map((m) => (
                    <button
                      key={m}
                      onClick={() => setMultiplier(m)}
                      className={`w-full py-1.5 text-xs font-bold rounded-lg transition-all ${
                        multiplier === m
                          ? 'bg-white text-cv-navy shadow-md'
                          : 'bg-white/10 text-white/85 hover:bg-white/20'
                      }`}
                    >
                      {m}x
                    </button>
                  ))}
                </div>
                {multiplier > 1 && !loading && baseMonthly != null && (
                  <p className="text-xs text-white/85 mt-2 text-center">
                    {symbol}{baseMonthly}/mo base &times; {multiplier}x
                    {isYearly && yearlyDiscountPercent != null && ` − ${yearlyDiscountPercent}% yearly discount`}
                  </p>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {PREMIUM_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-cv-gold" />
                    <span className="text-white/85 leading-snug">{f}</span>
                  </li>
                ))}
              </ul>

              {/* Payment info */}
              <div className="mb-4 flex items-center justify-center gap-4 text-xs text-white/85">
                <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Auto-pay or one-time</span>
                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Cancel anytime</span>
              </div>

              <a
                href={`${analyzerUrl}?premium=true`}
                className="w-full py-3.5 px-6 rounded-xl font-bold text-center text-sm transition-all flex items-center justify-center gap-2 bg-white text-cv-navy hover:bg-cv-sky/10 hover:shadow-lg"
              >
                Get Premium <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </ScrollReveal>
        </div>

        {/* Reserve Credits add-on */}
        <ScrollReveal delay={0.15}>
          <div className="mt-8 bg-cv-sky/20 rounded-2xl border border-cv-sky/40 p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🎯</span>
                  <h3 className="font-bold text-cv-navy">Reserve Credit Pool</h3>
                  <span className="px-2 py-0.5 bg-cv-gold/30 text-cv-navy text-xs font-bold rounded-full">Add-on</span>
                </div>
                <p className="text-sm text-cv-navy/70 mb-3">
                  Need more power? Purchase extra reserve credits that kick in when your daily allowance runs out. Available for all Premium users.
                </p>
                <div className="flex flex-wrap gap-3 text-xs text-cv-navy/70">
                  {(() => {
                    const pool = config?.reserve_pool ?? pricing?.reserve_pool;
                    const minC = pool?.min_credits;
                    const maxC = pool?.max_credits;
                    const inc = pool?.increment;
                    const priceUsd = pool?.price_per_credit_usd;
                    if (!Number.isFinite(Number(priceUsd)) || !Number.isFinite(Number(minC)) || !Number.isFinite(Number(maxC))) {
                      return (
                        <span className="bg-white px-3 py-1.5 rounded-lg border border-cv-sky/40 font-medium">
                          Loading pricing…
                        </span>
                      );
                    }
                    const sampleCredits = Math.max(Number(inc) || Number(minC) || 0, Number(minC) || 0) || 100;
                    const samplePrice = (sampleCredits * Number(priceUsd)).toFixed(2);
                    return (
                      <>
                        <span className="bg-white px-3 py-1.5 rounded-lg border border-cv-sky/40 font-medium">
                          {sampleCredits} credits = ${samplePrice}
                        </span>
                        <span className="bg-white px-3 py-1.5 rounded-lg border border-cv-sky/40 font-medium">
                          Min: {Number(minC).toLocaleString()} &middot; Max: {Number(maxC).toLocaleString()}
                        </span>
                      </>
                    );
                  })()}
                  <span className="bg-white px-3 py-1.5 rounded-lg border border-cv-sky/40 font-medium">
                    Top up anytime from Dashboard
                  </span>
                </div>
              </div>
              <a
                href={`${analyzerUrl}?premium=true`}
                className="cv-customer-cta cv-emboss shrink-0 px-6 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2"
              >
                Configure Plan <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={0.2}>
          <div className="text-center mt-8 space-y-2">
            <p className="text-sm text-cv-navy/60">
              All prices in USD. Indian users pay in INR via Razorpay. Coupons available at checkout.
            </p>
            <p className="text-xs text-cv-navy/40">
              Premium plans support configurable multipliers (1x-10x), monthly or yearly billing, and optional reserve credit pools.
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default PricingSection;
